const CartGiftLz = class extends HTMLElement {
  connectedCallback() {
    window.setTimeout(() => this.sync(), 0);
  }

  async sync() {
    if (CartGiftLz.syncPromise) {
      CartGiftLz.syncQueued = true;
      return CartGiftLz.syncPromise;
    }

    CartGiftLz.syncPromise = this._sync().finally(() => {
      CartGiftLz.syncPromise = null;

      if (CartGiftLz.syncQueued) {
        CartGiftLz.syncQueued = false;
        const currentManager = document.querySelector("cart-gift-lz");
        if (currentManager) window.setTimeout(() => currentManager.sync(), 0);
      }
    });

    return CartGiftLz.syncPromise;
  }

  async _sync() {
    const enabled = this.dataset.enabled === "true";
    const eligibleQuantity = Number(this.dataset.eligibleQuantity || 0);
    const threshold = Number(this.dataset.threshold || 1);
    const giftVariantId = Number(this.dataset.giftVariantId || 0);
    const giftLineKey = this.dataset.giftLineKey || "";
    const giftQuantity = Number(this.dataset.giftQuantity || 0);
    const giftAvailable = this.dataset.giftAvailable === "true";
    const giftDeclined = this.dataset.giftDeclined === "true";

    if (!giftVariantId) return;

    let request = null;

    if ((!enabled || giftDeclined || eligibleQuantity < threshold || !giftAvailable) && giftLineKey) {
      request = {
        url: `${window.Shopify.routes.root}cart/change.js`,
        body: { id: giftLineKey, quantity: 0 }
      };
    } else if (enabled && !giftDeclined && giftAvailable && eligibleQuantity >= threshold && !giftLineKey) {
      request = {
        url: `${window.Shopify.routes.root}cart/add.js`,
        body: {
          items: [{
            id: giftVariantId,
            quantity: 1,
            properties: {
              _auto_gift_lz: "true",
              _gift_rule_lz: `eligible-items-${threshold}`
            }
          }]
        }
      };
    } else if (enabled && !giftDeclined && eligibleQuantity >= threshold && giftLineKey && giftQuantity !== 1) {
      request = {
        url: `${window.Shopify.routes.root}cart/change.js`,
        body: { id: giftLineKey, quantity: 1 }
      };
    }

    if (!request) return;

    this._setCheckoutBusy(true);

    try {
      const pageSectionId = this.dataset.mainCartSectionId || "";
      const miniCartSectionId =
        this.closest("cart-drawer")?.getAttribute("section") ||
        document.querySelector("#mini-cart")?.getAttribute("section") ||
        "mini-cart";

      request.body.sections = [pageSectionId || miniCartSectionId];
      // Keep Shopify's preview-theme query parameters. Dropping them can make
      // section rendering return the published theme and remove this module.
      request.body.sections_url = `${window.location.pathname}${window.location.search}`;

      const response = await fetch(request.url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify(request.body)
      });

      const responseJson = await response.json();
      if (!response.ok) throw new Error(responseJson.description || "Gift synchronization failed");

      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        credentials: "same-origin"
      });
      const cart = await cartResponse.json();
      cart.sections = responseJson.sections || {};

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: { cart }
      }));

      if (pageSectionId && responseJson.sections?.[pageSectionId]) {
        const mainCart = this.closest(".shopify-section--main-cart");
        if (mainCart) mainCart.outerHTML = responseJson.sections[pageSectionId];
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
        bubbles: true,
        detail: { cart, replacementDelay: 0 }
      }));
    } catch (error) {
      console.error("Cart gift sync:", error);
      document.documentElement.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        detail: { status: "error", error: error.message }
      }));
    } finally {
      this._setCheckoutBusy(false);
    }
  }

  _setCheckoutBusy(isBusy) {
    document.querySelectorAll('[name="checkout"], .checkout-button').forEach((button) => {
      button.toggleAttribute("disabled", isBusy);
      button.setAttribute("aria-busy", String(isBusy));
    });
  }
};

CartGiftLz.syncPromise = null;
CartGiftLz.syncQueued = false;

if (!window.customElements.get("cart-gift-lz")) {
  window.customElements.define("cart-gift-lz", CartGiftLz);
}

const CartShippingSummaryLz = class extends HTMLElement {
  connectedCallback() {
    this.fit = () => {
      const country = this.querySelector("[data-shipping-country]");
      if (!country) return;
      const decode = (value) => {
        const textarea = document.createElement("textarea");
        textarea.innerHTML = value || "";
        return textarea.value;
      };
      country.textContent = decode(country.dataset.full);
      const header = this.closest(".drawer__header");
      if (!header) return;
      window.requestAnimationFrame(() => {
        const title = header.querySelector(".drawer__title");
        const close = header.querySelector(".drawer__close-button");
        if (!title || !close) return;
        const available = close.getBoundingClientRect().left - title.getBoundingClientRect().right - 18;
        if (this.scrollWidth > available) country.textContent = decode(country.dataset.short);
      });
    };
    this.fit();
    document.fonts?.ready.then(this.fit);
    this.resizeObserver = new ResizeObserver(this.fit);
    this.resizeObserver.observe(this.closest(".drawer__header") || this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }
};

if (!window.customElements.get("cart-shipping-summary-lz")) {
  window.customElements.define("cart-shipping-summary-lz", CartShippingSummaryLz);
}

document.addEventListener("click", (event) => {
  const openButton = event.target.closest("[data-gift-terms-open]");
  if (openButton) {
    const dialog = document.getElementById(openButton.dataset.giftTermsOpen);
    dialog?.showModal();
    return;
  }
  const closeButton = event.target.closest("[data-gift-terms-close]");
  if (closeButton) closeButton.closest("[data-gift-terms-dialog]")?.close();
});

document.addEventListener("click", (event) => {
  const dialog = event.target.closest("[data-gift-terms-dialog]");
  if (dialog && event.target === dialog) dialog.close();
});

document.addEventListener("click", async (event) => {
  const button = event.target.closest("[data-gift-choice]");
  if (!button || button.disabled) return;
  const declined = button.dataset.declined === "true";
  const lineKey = button.dataset.giftLineKey || "";
  const pageSection = button.closest("section[section-id]");
  const pageSectionId = pageSection?.getAttribute("section-id") || "";
  const drawerSectionId = button.closest("cart-drawer")?.getAttribute("section") || document.querySelector("#mini-cart")?.getAttribute("section") || "mini-cart";
  const sectionIds = [...new Set([pageSectionId || drawerSectionId].filter(Boolean))];
  const body = {
    attributes: { _gift_declined_lz: declined ? "" : "true" },
    sections: sectionIds,
    sections_url: `${window.location.pathname}${window.location.search}`
  };
  if (!declined && lineKey) body.updates = { [lineKey]: 0 };
  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(body)
    });
    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || "Gift preference update failed");
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: { cart } }));
    if (pageSectionId && cart.sections?.[pageSectionId]) {
      const mainCart = button.closest(".shopify-section--main-cart");
      if (mainCart) mainCart.outerHTML = cart.sections[pageSectionId];
    } else {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true, detail: { cart, replacementDelay: 0 } }));
    }
  } catch (error) {
    button.disabled = false;
    button.removeAttribute("aria-busy");
    document.documentElement.dispatchEvent(new CustomEvent("cart-notification:show", { bubbles: true, detail: { status: "error", error: error.message } }));
  }
});

const SPECIAL_OFFER_END_KEY_LZ = "oapor:special-offer-ends-at";

const OfferCountdownLz = class extends HTMLElement {
  connectedCallback() {
    this.timeElement = this.querySelector("[data-countdown-time]");
    const durationMinutes = Math.max(1, Number(this.dataset.durationMinutes || 20));

    try {
      this.endsAt = Number(window.localStorage.getItem(SPECIAL_OFFER_END_KEY_LZ));
      if (!this.endsAt) {
        this.endsAt = Date.now() + durationMinutes * 60 * 1000;
        window.localStorage.setItem(SPECIAL_OFFER_END_KEY_LZ, String(this.endsAt));
      }
    } catch (error) {
      this.endsAt = Date.now() + durationMinutes * 60 * 1000;
    }

    this._render();
    if (this.endsAt > Date.now()) {
      this.hidden = false;
      this.timer = window.setInterval(() => this._render(), 1000);
    }
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  _render() {
    const remainingSeconds = Math.max(0, Math.ceil((this.endsAt - Date.now()) / 1000));
    if (remainingSeconds <= 0) {
      this.hidden = true;
      window.clearInterval(this.timer);
      return;
    }

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = String(remainingSeconds % 60).padStart(2, "0");
    if (this.timeElement) this.timeElement.textContent = `${minutes}:${seconds}`;
  }
};

if (!window.customElements.get("offer-countdown-lz")) {
  window.customElements.define("offer-countdown-lz", OfferCountdownLz);
}

const clearSpecialOfferCountdownLz = (cart) => {
  if (Number(cart?.item_count) > 0) return;
  try {
    window.localStorage.removeItem(SPECIAL_OFFER_END_KEY_LZ);
  } catch (error) {
    // Storage may be unavailable in private browsing; no reset is needed there.
  }
};

if (Number(window.themeVariables?.settings?.cartCount) === 0) {
  clearSpecialOfferCountdownLz({ item_count: 0 });
}

document.documentElement.addEventListener("cart:updated", (event) => {
  clearSpecialOfferCountdownLz(event.detail?.cart);
});

const CartDiscountLz = class extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[data-discount-input]");
    this.applyButton = this.querySelector("[data-discount-apply]");
    this.removeButtons = Array.from(this.querySelectorAll("[data-discount-remove]"));
    this.status = this.querySelector("[data-discount-status]");
    this.invalidTemplate = this.querySelector("[data-discount-invalid-template]");
    this.longCodeTemplate = this.querySelector("[data-discount-long-code-template]");

    this.applyButton?.addEventListener("click", () => this.apply());
    this.removeButtons.forEach((button) => {
      button.addEventListener("click", () => this.remove(button.closest("[data-discount-code]")?.dataset.discountCode));
    });
    this.input?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      this.apply();
    });
  }

  async apply() {
    const code = this.input?.value.trim() || "";

    if (!code) {
      this._showInvalidStatus();
      this.input?.focus();
      return;
    }

    const existingCodes = this._existingCodes();
    const isDuplicate = existingCodes.some((existingCode) => existingCode.toLocaleLowerCase() === code.toLocaleLowerCase());
    if (isDuplicate) return;

    await this._update([...existingCodes, code], code, existingCodes);
  }

  async remove(codeToRemove) {
    if (!codeToRemove) return;
    const remainingCodes = this._existingCodes().filter((code) => code !== codeToRemove);
    await this._update(remainingCodes, "", remainingCodes);
  }

  async _update(codes, expectedCode, previousCodes) {
    if (this.isBusy) return;
    this.isBusy = true;
    this._setBusy(true);
    this._showStatus("", false);

    try {
      const sectionId = this.dataset.sectionId;
      let cart = await this._submitCodes(codes, sectionId);

      if (expectedCode) {
        const normalizedCode = expectedCode.toLocaleLowerCase();
        const discountCodeState = (cart.discount_codes || []).find((discountCode) => {
          return String(discountCode.code || "").toLocaleLowerCase() === normalizedCode;
        });
        const listedAsApplied = (cart.cart_level_discount_applications || []).some((application) => {
          return application.type === "discount_code" &&
            String(application.title || "").toLocaleLowerCase() === normalizedCode;
        });
        const isApplicable = discountCodeState
          ? discountCodeState.applicable === true
          : listedAsApplied;

        if (!isApplicable) {
          await this._submitCodes(previousCodes, "");
          this._showInvalidStatus();
          return;
        }
      }

      const applicableCodes = this._applicableCodes(cart, codes);
      if (applicableCodes.length !== codes.length) {
        cart = await this._submitCodes(applicableCodes, sectionId);
      }

      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: { cart }
      }));

      this._refresh(cart);
    } catch (error) {
      console.error("Cart discount:", error);
      this._showStatus(error.message || this.dataset.errorMessage, true);
    } finally {
      this.isBusy = false;
      this._setBusy(false);
    }
  }

  async _submitCodes(codes, sectionId) {
    const body = {
      discount: codes.join(","),
      attributes: {
        _discount_codes_lz: codes.join(","),
        _discount_code_lz: ""
      }
    };

    if (sectionId) {
      body.sections = [sectionId];
      body.sections_url = `${window.location.pathname}${window.location.search}`;
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify(body)
    });

    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || this.dataset.errorMessage);
    return cart;
  }

  _applicableCodes(cart, submittedCodes) {
    if (Array.isArray(cart.discount_codes)) {
      const applicable = new Set(
        cart.discount_codes
          .filter((discountCode) => discountCode.applicable === true)
          .map((discountCode) => String(discountCode.code || "").toLocaleLowerCase())
      );
      return submittedCodes.filter((code) => applicable.has(code.toLocaleLowerCase()));
    }

    const appliedTitles = new Set();
    (cart.cart_level_discount_applications || []).forEach((application) => {
      if (application.type === "discount_code") {
        appliedTitles.add(String(application.title || "").toLocaleLowerCase());
      }
    });
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        const application = allocation.discount_application;
        if (application?.type === "discount_code") {
          appliedTitles.add(String(application.title || "").toLocaleLowerCase());
        }
      });
    });
    return submittedCodes.filter((code) => appliedTitles.has(code.toLocaleLowerCase()));
  }

  _refresh(cart) {
    const sectionId = this.dataset.sectionId;
    const html = cart.sections?.[sectionId];

    if (this.dataset.context === "page" && html) {
      const mainCart = this.closest(".shopify-section--main-cart");
      if (mainCart) mainCart.outerHTML = html;
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
      bubbles: true,
      detail: { cart, replacementDelay: 0 }
    }));
  }

  _setBusy(isBusy) {
    this.toggleAttribute("aria-busy", isBusy);
    this.input?.toggleAttribute("disabled", isBusy);
    this.applyButton?.toggleAttribute("disabled", isBusy);
    this.removeButtons.forEach((button) => button.toggleAttribute("disabled", isBusy));
    document.querySelectorAll('[name="checkout"], .checkout-button').forEach((button) => {
      button.toggleAttribute("disabled", isBusy);
      button.setAttribute("aria-busy", String(isBusy));
    });
  }

  _showStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message || "";
    this.status.classList.toggle("is-error", Boolean(message && isError));
  }

  _showInvalidStatus(code = this.input?.value.trim() || "") {
    if (!this.status) return;
    this.status.replaceChildren();
    const messageTemplate = code.length > 15 ? this.longCodeTemplate : this.invalidTemplate;
    if (messageTemplate?.content) {
      this.status.append(messageTemplate.content.cloneNode(true));
    } else {
      this.status.textContent = this.dataset.invalidMessage || "";
    }
    this.status.classList.add("is-error");
  }

  _existingCodes() {
    return Array.from(this.querySelectorAll("[data-discount-code]"))
      .map((element) => element.dataset.discountCode?.trim())
      .filter(Boolean);
  }
};

if (!window.customElements.get("cart-discount-lz")) {
  window.customElements.define("cart-discount-lz", CartDiscountLz);
}

// Checkout back-navigation can restore a frozen cart page from the browser's
// back-forward cache. Re-render the cart section from Shopify before allowing
// the restored page to continue so gift eligibility and checkout state are fresh.
window.addEventListener("pageshow", async (event) => {
  if (!event.persisted) return;

  const manager = document.querySelector('cart-gift-lz[data-context="page"]');
  const sectionId = manager?.dataset.mainCartSectionId;
  const mainCart = manager?.closest(".shopify-section--main-cart");
  if (!sectionId || !mainCart) return;

  mainCart.querySelectorAll('[name="checkout"], .checkout-button').forEach((button) => {
    button.setAttribute("disabled", "");
    button.setAttribute("aria-busy", "true");
  });

  try {
    const sectionUrl = new URL(window.location.href);
    sectionUrl.searchParams.set("section_id", sectionId);
    const response = await fetch(sectionUrl.toString(), { credentials: "same-origin" });
    if (!response.ok) throw new Error("Cart page refresh failed");
    mainCart.outerHTML = await response.text();
  } catch (error) {
    console.error("Cart page gift refresh:", error);
    document.documentElement.dispatchEvent(new CustomEvent("cart-notification:show", {
      bubbles: true,
      detail: { status: "error", error: "Please refresh the cart before checkout." }
    }));
  }
});
